import React, { useRef } from 'react';
import { arrowRIcon, dots3VerticalIcon } from '../../static/svgs';
import { formatBadgeCount } from '../../utils/badgeCounts';
import CompanyPaymentBlock from './CompanyPaymentBlock';
import './companyPicker.css';

const LONG_PRESS_MS = 500;

const SERVICE_INFO_TIP =
    'Для перехода компании на постоянное бухгалтерское обслуживание, необходимо написать об этом письмо в чате и согласовать все условия';

function getServiceStatusLabel(onService) {
    return onService
        ? 'На постоянном бухгалтерском обслуживании'
        : 'Может перейти на постоянное обслуживание';
}

function CompanyPickerList({
    users,
    onSelect,
    className = '',
    badgeByUserId = null,
    onMenuClick = null,
    onPaymentClick = null,
    showPaymentInfo = false,
    showServiceStatus = false,
    paymentByUserId = null,
    onMarkUnread = null,
    itemClickable = true,
    hideArrow = false,
    renderItemTrailing = null,
    renderItemExtra = null,
}) {
    const longPressTimerRef = useRef(null);
    const longPressTriggeredRef = useRef(false);

    if (!users || users.length === 0) {
        return null;
    }

    const clearLongPress = () => {
        if (longPressTimerRef.current) {
            clearTimeout(longPressTimerRef.current);
            longPressTimerRef.current = null;
        }
    };

    const openMarkUnread = (user, clientX, clientY) => {
        if (!onMarkUnread) return;
        onMarkUnread(user, { x: clientX, y: clientY });
    };

    const handleContextMenu = (e, user) => {
        if (!onMarkUnread) return;
        e.preventDefault();
        e.stopPropagation();
        openMarkUnread(user, e.clientX, e.clientY);
    };

    const handleTouchStart = (e, user) => {
        if (!onMarkUnread) return;
        longPressTriggeredRef.current = false;
        const touch = e.touches?.[0];
        if (!touch) return;
        const { clientX, clientY } = touch;
        clearLongPress();
        longPressTimerRef.current = setTimeout(() => {
            longPressTriggeredRef.current = true;
            openMarkUnread(user, clientX, clientY);
        }, LONG_PRESS_MS);
    };

    const handleTouchEnd = () => {
        clearLongPress();
    };

    const handleTouchMove = () => {
        clearLongPress();
    };

    return (
        <div className={`company-picker-list company-picker-panel ${className}`.trim()}>
            {users.map((user) => {
                const badge = badgeByUserId
                    ? formatBadgeCount(badgeByUserId[user.user_id])
                    : null;
                const payment = showPaymentInfo ? paymentByUserId?.[user.user_id] : null;
                const withExtra = showPaymentInfo || showServiceStatus;
                const withChats = typeof renderItemExtra === 'function';
                const HeaderTag = itemClickable ? 'button' : 'div';
                const headerProps = itemClickable
                    ? {
                        type: 'button',
                        onClick: () => {
                            if (longPressTriggeredRef.current) {
                                longPressTriggeredRef.current = false;
                                return;
                            }
                            if (onSelect) onSelect(user);
                        },
                    }
                    : {};

                const header = (
                    <HeaderTag
                        className={`company-picker-main-btn manrope-bold${withExtra ? ' company-picker-main-btn--with-payment' : ''}${itemClickable ? '' : ' company-picker-main-btn--static'}`}
                        {...headerProps}
                    >
                        <div className="company-picker-main-top">
                            <span className="company-picker-name">{user.company_name}</span>
                            <span className="company-picker-trailing">
                                {badge && (
                                    <span className="company-picker-badge">{badge}</span>
                                )}
                                {typeof renderItemTrailing === 'function' ? renderItemTrailing(user) : null}
                                {!hideArrow && arrowRIcon}
                            </span>
                        </div>
                        {showPaymentInfo && payment && (
                            <CompanyPaymentBlock
                                payment={payment}
                                user={user}
                                onPaymentClick={onPaymentClick}
                            />
                        )}
                        {showServiceStatus && (
                            <div className="company-picker-service-meta">
                                <div className="company-picker-service-row">
                                    <span className="company-picker-service-label">Статус</span>
                                    <span className="company-picker-service-value">
                                        <span>{getServiceStatusLabel(!!user.on_service)}</span>
                                        {!user.on_service ? (
                                            <span
                                                className="company-picker-status-info"
                                                aria-label={SERVICE_INFO_TIP}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <span className="company-picker-status-info-btn" aria-hidden="true">
                                                    i
                                                </span>
                                                <span className="company-picker-status-info-tip" role="tooltip">
                                                    {SERVICE_INFO_TIP}
                                                </span>
                                            </span>
                                        ) : null}
                                    </span>
                                </div>
                            </div>
                        )}
                    </HeaderTag>
                );

                return (
                <div
                    key={user.user_id}
                    className={`company-picker-item${withExtra ? ' company-picker-item--with-payment' : ''}${withChats ? ' company-picker-item--with-chats' : ''}`}
                    onContextMenu={onMarkUnread ? (e) => handleContextMenu(e, user) : undefined}
                    onTouchStart={onMarkUnread ? (e) => handleTouchStart(e, user) : undefined}
                    onTouchEnd={onMarkUnread ? handleTouchEnd : undefined}
                    onTouchMove={onMarkUnread ? handleTouchMove : undefined}
                    onTouchCancel={onMarkUnread ? handleTouchEnd : undefined}
                >
                    {onMenuClick && (
                        <button
                            type="button"
                            className="company-picker-menu-btn"
                            aria-label={`Меню ${user.company_name}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                onMenuClick(user);
                            }}
                        >
                            {dots3VerticalIcon}
                        </button>
                    )}
                    {withChats ? (
                        <div className="company-picker-item-body">
                            {header}
                            {renderItemExtra(user)}
                        </div>
                    ) : header}
                </div>
                );
            })}
        </div>
    );
}

export default CompanyPickerList;
